
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/ui/user-avatar';
import { MentionUser } from './Mention';

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export interface MentionListProps {
  items: MentionUser[];
  command: (item: MentionUser) => void;
}

const GROUPS: Array<{
  source: NonNullable<MentionUser['source']>;
  label: string;
}> = [
  { source: 'internal', label: 'Internal' },
  { source: 'external', label: 'External' },
  { source: 'portal', label: 'Portal Users' },
];

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const groupedItems = useMemo(() => {
      const normalizedItems = items.map(item => ({
        ...item,
        source: item.source ?? 'internal',
      }));

      return GROUPS.map(group => ({
        ...group,
        items: normalizedItems.filter(item => item.source === group.source),
      })).filter(group => group.items.length > 0);
    }, [items]);
    const selectableItems = useMemo(
      () => groupedItems.flatMap(group => group.items),
      [groupedItems],
    );

    // Reset selection when items change
    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    const selectItem = useCallback((index: number) => {
      const item = selectableItems[index];
      if (item) {
        command(item);
      }
    }, [command, selectableItems]);

    // Expose keyboard handler to parent
    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev <= 0 ? selectableItems.length - 1 : prev - 1
          );
          return true;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev >= selectableItems.length - 1 ? 0 : prev + 1
          );
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          event.stopPropagation();
          selectItem(selectedIndex);
          return true;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          return true;
        }

        return false;
      },
    }), [selectableItems, selectedIndex, selectItem]);

    if (items.length === 0) {
      return (
        <div className="mention-list-empty p-3 text-sm text-muted-foreground">
          No users found
        </div>
      );
    }

    return (
      <div className="mention-list overflow-hidden">
        {groupedItems.map((group) => {
          const groupStartIndex = selectableItems.findIndex(
            item => item.source === group.source,
          );

          return (
            <div key={group.source} className="mention-list-group">
              <div className="mention-list-group-label px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </div>
              {group.items.map((item, index) => {
                const flatIndex = groupStartIndex + index;

                return (
                  <button
                    key={item.id}
                    className={cn(
                      'mention-list-item w-full flex items-center gap-3 px-3 py-2',
                      'text-left text-sm transition-colors',
                      'hover:bg-muted/50',
                      flatIndex === selectedIndex && 'bg-muted'
                    )}
                    onClick={() => selectItem(flatIndex)}
                    onMouseDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onMouseEnter={() => setSelectedIndex(flatIndex)}
                    type="button"
                  >
                    {/* Avatar */}
                    <UserAvatar
                      user={{ id: item.id, displayName: item.name, name: item.name, email: item.email }}
                      size="sm"
                      className="shrink-0"
                    />

                    {/* User info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {item.email}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
);

MentionList.displayName = 'MentionList';
